import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { pick } from 'lodash-es'
import { whiteboardConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import utils from '@/services/utils'
import appConfig from '@/services/appConfig'

interface IWbTextBoxOptions extends fabric.ITextboxOptions, IWbObjectProps {
  truncate?: boolean
}

export default class WbTextBox extends fabric.Textbox implements IWbObject {
  public id: string
  public type = whiteboardConstants.objectTypes.textBox
  public lock: boolean
  public connectable: boolean
  public localOnly: boolean
  public preventUnlock: boolean
  public truncate: boolean
  public excludeFromGroupSelection?: boolean | undefined
  public preventDelete?: boolean
  public canBeLinkedTo = true
  public canLink = false
  public link = undefined

  private _origFontSize?: number
  private _origWidth?: number

  public editableProps: Record<string, IWbObjectProp> = {
    backgroundColor: { name: 'background color', type: 'backgroundColor', editable: true },
    textColor: { name: 'text color', type: 'textColor', editable: true },
    font: { name: 'font', type: 'font', editable: true },
    fontSize: { name: 'font size', type: 'fontSize', editable: true },
    textAlign: { name: 'align', type: 'textAlign', editable: true },
    fontStyle: { name: 'font style', type: 'fontStyle', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    addDiscussion: { name: 'add comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
    info: { name: 'info', type: 'info', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    cloneAsImage: { action: 'cloneAsImage', label: 'Copy as Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  constructor(text: string, opt?: IWbTextBoxOptions) {
    super(text, opt)
    const userStore = useUserStore()
    const userWhiteboardSettings = utils.isDefined(userStore.settingsContainer) && userStore.settingsContainer.whiteboard ? userStore.settingsContainer.whiteboard : ''
    this.id = opt?.id || guid()
    this.lock = opt?.lock || false
    this.editable = !opt?.lock
    this.connectable = true
    this.localOnly = opt?.localOnly || false
    this.preventUnlock = opt?.preventUnlock || false
    this.truncate = opt?.truncate || false
    this.excludeFromGroupSelection = opt?.excludeFromGroupSelection || false
    this.preventDelete = opt?.preventDelete || false
    this.fontFamily = opt?.fontFamily
      ? opt?.fontFamily
      : (userWhiteboardSettings !== '' && userWhiteboardSettings.textBoxFont && userWhiteboardSettings.textBoxFont !== '')
          ? userWhiteboardSettings.textBoxFont
          : utils.isDefined(appConfig.dynamicConfig) && utils.isDefined(appConfig.dynamicConfig.whiteboardDefaultFont) && appConfig.dynamicConfig.whiteboardDefaultFont.length
            ? appConfig.dynamicConfig.whiteboardDefaultFont
            : 'Helvetica'

    this.setLock(this.lock)
    this.stateProperties = this.stateProperties?.concat(['lock', 'localOnly', 'preventUnlock', 'selectable'])

    this.on('scaling', () => {
      if (!this._origFontSize) {
        this._origFontSize = this.fontSize
        this._origWidth = this.width
      }
    })

    this.on('modified', () => {
      const scaleX = this.scaleX || 1
      const scaleY = this.scaleY || 1

      this.width = Math.round((this._origWidth || this.width || 0) * scaleX)
      this.scaleX = 1
      this.scaleY = 1
      this.setProp('fontSize', { fontSize: Math.round((this._origFontSize || this.fontSize || 0) * scaleY) })

      this.setCoords()

      this._origFontSize = undefined
      this._origWidth = undefined
    })
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false, notifyObjectModified: boolean = true) {
    switch (prop) {
      case 'textColor':
        this.set('fill', value.textColor)
        break
      case 'font':
        this.set('fontFamily', value.font)
        break
      case 'fontSize':
        this.set('fontSize', value.fontSize)
        break
      case 'textAlign':
        this.set('textAlign', value.textAlign)
        break
      case 'bold':
        this.set('fontWeight', value.bold ? 'bold' : '')
        break
      case 'italic':
        this.set('fontStyle', value.italic ? 'italic' : '')
        break
      case 'underline':
        this.set('underline', value.underline)
        break
      case 'text':
        this.set('text', value.text)
        break
      case 'lock':
        this.set('lock', value.lock)
        this.setLock(value.lock)
        break
      case 'backgroundColor':
        this.set('backgroundColor', value.backgroundColor)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    if (notifyObjectModified) {
      this.canvas?.fire('object:modified', { target: this, ignoreHistory })
    }
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'textColor':
        result.textColor = this.fill
        break
      case 'font':
        result.font = this.fontFamily
        break
      case 'fontSize':
        result.fontSize = this.fontSize
        break
      case 'textAlign':
        result.textAlign = this.textAlign
        break
      case 'bold':
        result.bold = !!this.fontWeight
        break
      case 'italic':
        result.italic = !!this.fontStyle
        break
      case 'underline':
        result.underline = !!this.underline
        break
      case 'text':
        result.text = this.text
        break
      case 'lock':
        result.lock = this.lock
        break
      case 'backgroundColor':
        result.backgroundColor = this.backgroundColor
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
    this.set('editable', !lock)
  }

  override _wrapText(lines: string[], desiredWidth: number): string[][] {
    if (this.truncate) {
      const newLines = lines.map((_line, lineIndex) => {
        const offset = 0
        let textWidth = this._measureWord([..._line], lineIndex, offset)

        if (textWidth > desiredWidth && !this.isEditing) {
          const ellipsis = '…'
          const lengthReducingFactor = desiredWidth / textWidth
          let length = Math.ceil(_line.length * lengthReducingFactor)
          while (textWidth >= desiredWidth && length > 0) {
            _line = _line.substring(0, length--) + ellipsis
            textWidth = this._measureWord([..._line], lineIndex, offset)
          }
        }
        return _line
      })
      return super._wrapText(newLines, desiredWidth)
    }
    return super._wrapText(lines, desiredWidth)
  }

  override onKeyDown(e: Event): void {
    e.stopPropagation()
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'angle',
      'text',
      'fill',
      'backgroundColor',
      'fontFamily',
      'fontSize',
      'fontStyle',
      'underline',
      'fontWeight',
      'textAlign',
      'lock',
      'localOnly',
      'version',
      'preventUnlock',
      'selectable',
    ]
    return pick(this, propsToPluck)
  }

  static fromObject(object: fabric.Object, callback?: Function) {
    return fabric.Object._fromObject(whiteboardConstants.objectTypes.textBox, object, callback, 'text') as WbTextBox
  }
}

const f: any = fabric
f.WbTextBox = WbTextBox
